import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { catalogApi, watchesApi } from '@/lib/api'
import type { Collection, Watch } from '@/types'

/**
 * The catalogue, read ONCE and shared by every component that needs it.
 *
 * A tiny module-level store rather than a fetch per hook: the header search,
 * the home grid and the studio all mount at the same time, and three copies of
 * the same table would mean three reads and — worse — three versions of the
 * truth after the owner edits a listing. Writing through this hook refreshes
 * everyone at once.
 */

interface CatalogueState {
  watches: Watch[]
  collections: Collection[]
  loading: boolean
}

let state: CatalogueState = { watches: [], collections: [], loading: true }
let inflight: Promise<void> | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): CatalogueState {
  return state
}

async function fetchCatalogue(): Promise<void> {
  const [watches, collections] = await Promise.all([
    catalogApi.watches(),
    catalogApi.collections(),
  ])
  state = {
    watches: watches.slice(),
    collections: collections.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    loading: false,
  }
  emit()
}

/**
 * Load the catalogue. Concurrent callers share one request — on first paint
 * several components ask at the same moment and only one should hit the wire.
 */
export function loadCatalogue(force = false): Promise<void> {
  if (inflight) return inflight
  if (!force && !state.loading) return Promise.resolve()
  inflight = fetchCatalogue()
    .catch(() => {
      // A failed read must not leave the whole site spinning forever.
      state = { ...state, loading: false }
      emit()
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

export function useWatches() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    void loadCatalogue()
  }, [])

  const reload = useCallback(() => loadCatalogue(true), [])

  // ── Writes (studio only) ───────────────────────────────────────────────────
  const create = useCallback(async (data: Partial<Watch>) => {
    const row = await watchesApi.create(data)
    await loadCatalogue(true)
    return row
  }, [])

  const update = useCallback(async (id: string, data: Partial<Watch>) => {
    const row = await watchesApi.update(id, data)
    await loadCatalogue(true)
    return row
  }, [])

  const remove = useCallback(async (id: string) => {
    await watchesApi.remove(id)
    await loadCatalogue(true)
  }, [])

  return {
    watches: snapshot.watches,
    collections: snapshot.collections,
    loading: snapshot.loading,
    reload,
    create,
    update,
    remove,
  }
}

/** One piece by id, with its collection resolved. Used by the detail page. */
export function useWatch(id: string | undefined) {
  const { watches, collections, loading, reload } = useWatches()

  const watch = useMemo(() => watches.find((w) => w.id === id) ?? null, [watches, id])
  const collection = useMemo(
    () => collections.find((c) => c.slug === watch?.collection) ?? null,
    [collections, watch],
  )

  return { watch, collection, watches, collections, loading, reload }
}

/**
 * Count a view, once per browser per piece. Deliberately fire-and-forget and
 * kept out of the render path: a failed counter must never break a page.
 */
export function useViewCounter(watch: Watch | null) {
  const [counted, setCounted] = useState<string | null>(null)

  useEffect(() => {
    if (!watch || counted === watch.id) return
    const key = `tempora-viewed:${watch.id}`
    try {
      if (sessionStorage.getItem(key)) return
      sessionStorage.setItem(key, '1')
    } catch {
      return /* storage blocked — skip counting rather than double-count */
    }
    setCounted(watch.id)
    void watchesApi.incrementViews(watch.id).catch(() => undefined)
  }, [watch, counted])
}
